<template>
  <v-row class="ma-0" justify="center">
    <v-dialog v-model="dialog"
              @click:outside="$emit('closeDialog')"
              @keydown="value => value.key === 'Escape' ? $emit('closeDialog') : ''"
              scrollable max-width="700px">

      <v-card class="pa-1">
        <!-- card title -->
        <v-card-title class="d-flex justify-center">
          <h3 class="main--text">{{ translations[isUpdate ? 'updateProperty' : 'newProperty'] }}</h3>
        </v-card-title>

        <v-card-text>
          <!-- section category -->
          <div class="section-divider mt-4">
            <div class="section-divider-title">{{ translations.property }}</div>

            <!-- key -->
            <v-col class="pa-0 mt-4">
              <b class="ml-8">{{ translations.key }} *</b>
              <v-text-field v-model="selectedItem.key" prepend-icon="mdi-key"
                            :rules="[requiredRule]"
                            class="mt-1" outlined dense>
              </v-text-field>
            </v-col>

            <!-- type -->
            <v-col class="pa-0 mt-1">
              <b class="ml-8">{{ translations.type }} *</b>
              <v-select v-model="selectedItem.type" prepend-icon="mdi-form-select"
                        :items="propertyTypeOptions"
                        class="mt-1" outlined dense>
              </v-select>
            </v-col>

            <!-- value -->
            <v-col class="pa-0 mt-1">
              <b class="ml-8">{{ translations.value }}</b>
              <template v-if="selectedItem.type === 'STRING'">
                <v-text-field v-model="selectedItem.value" prepend-icon="mdi-text"
                              class="mt-1" outlined dense>
                </v-text-field>
              </template>

              <template v-if="selectedItem.type === 'INTEGER'">
                <v-text-field v-model="selectedItem.value" prepend-icon="mdi-text"
                              :rules="[onlyDigitsRule]"
                              class="mt-1" outlined dense>
                </v-text-field>
              </template>

              <template v-if="selectedItem.type === 'JSON'">
                <v-textarea v-model="selectedItem.value" prepend-icon="mdi-code-json"
                            :rules="[jsonValidRule]" rows="10"
                            class="mt-1 textarea-small" outlined dense>
                </v-textarea>
              </template>

              <template v-if="selectedItem.type === 'ENUM'">
                <v-select v-model="selectedItem.value" prepend-icon="mdi-view-list"
                          :items="enumOptions"
                          class="mt-1" outlined dense>
                </v-select>
              </template>

              <template v-if="selectedItem.type === 'BOOLEAN'">
                <v-switch v-model="selectedItem.value" :prepend-icon="selectedItem.value ? 'mdi-toggle-switch-variant': 'mdi-toggle-switch-variant-off'"
                          class="mt-1" dense inset />
              </template>
            </v-col>
          </div>
        </v-card-text>

        <!-- card actions -->
        <v-card-actions class="pb-4 pt-4">
          <v-row class="ma-0" justify="space-around">
            <v-btn @click="$emit('closeDialog')" class="px-8" outlined :loading="loading">{{ translations.cancel }}</v-btn>

            <v-btn @click="confirm" :disabled="disableBtn" :loading="loading"
                   color="main" class="white--text px-8">
              {{ translations.confirm }}
            </v-btn>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>


    <!-- message dialog -->
    <message-dialog :show="showErrorMsgDialog">
      <template v-slot:card-image>
        <div class="d-flex justify-center cursor-pointer pt-2" @click="showErrorMsgDialog = false">
          <warning-svg :brandColor="$vuetify.theme.themes.light.main" width="200px"></warning-svg>
        </div>
      </template>

      <template v-slot:message>
        <div class="d-flex justify-center cursor-pointer pt-1" @click="showErrorMsgDialog = false">
          {{ translations.saveError }}
        </div>
      </template>

      <template v-slot:card-action>
        <div class="d-flex justify-center cursor-pointer" style="width: 100%">
          <v-btn color="main" class="white--text" text @click="showErrorMsgDialog = false">{{ translations.ok }}</v-btn>
        </div>
      </template>
    </message-dialog>
  </v-row>
</template>


<script>
import { translations } from '@/utils/common'
import { Rules } from '@/utils/mixins/Rules'

import PropertyService from '@/service/property/PropertyService'

import MessageDialog from '@/components/MessageDialog'
import WarningSvg from '@/assets/WarningSvg'

export default {
  name: 'PropertySaveEditDialog',

  components: {
    MessageDialog,
    WarningSvg
  },

  props: {
    item: {
      type: Object,
      default: () => {
      }
    },

    show: {
      type: Boolean,
      default: false,
      required: true
    },

    update: {
      type: Boolean,
      default: false,
      required: true
    }
  },

  data() {
    return {
      translations: translations,

      dialog: false,
      loading: false,
      isUpdate: false,
      showErrorMsgDialog: false,

      selectedItem: {},
      propertyTypeOptions: [],
      enumOptions: []
    }
  },

  created() {
    this.initPropertyTypeOptions()
    this.setItem()
  },

  computed: {
    disableBtn() {
      return !(
        (this.validRequired(this.selectedItem.key))
        && (this.selectedItem.type !== 'JSON' || this.jsonValidRule(this.selectedItem.value) === true)
        && (this.selectedItem.type !== 'INTEGER' || this.onlyDigitsRule(this.selectedItem.value) === true)
      )
    }
  },

  mixins: [
    Rules
  ],

  methods: {
    async initPropertyTypeOptions() {
      this.propertyTypeOptions = await PropertyService.getEnumValues('propertyType')
    },

    async setItem() {
      if (this.item.key) {
        this.selectedItem = { ...this.item }
        delete this.selectedItem['__typename']
        if (this.selectedItem.type === 'BOOLEAN') {
          this.selectedItem.value = eval(this.selectedItem.value)
        }
      } else {
        this.selectedItem = {}
      }

      // init enum options
      this.enumOptions = await PropertyService.getEnumValues(this.selectedItem.key)
    },

    async confirm() {
      this.loading = true

      await PropertyService.save({
          propertyDTO: this.selectedItem
        })
        .then(() => {
          this.$root.vtoast.show({ message: translations.updateSuccess })
          this.$emit('updated')
        })
        .catch(() => {
          this.showErrorMsgDialog = true
        })
        .finally(() => {
          this.loading = false
          this.$emit('closeDialog')
        })
    }
  },

  watch: {
    show() {
      this.dialog = this.show
    },

    update() {
      this.isUpdate = this.update
    },

    item() {
      this.setItem()
    }
  }
}
</script>


<style>
.textarea-small, textarea {
  color: rgba(0, 0, 0, 0.6) !important;
  line-height: 20px !important;
  font-size: small !important;
}
</style>
